import React, { useEffect, useMemo, useState } from 'react';
import styles from '../llmSettingsTab/LlmSettingsTab.module.css';
import { settingsApi } from '../../../lib/apiClient';
import { toast } from '../../../lib/toast';
import { FieldMeta, SettingsMap } from '../../../types/webchatTabsTypes';
import SecureInput from '../../../ui/SecureInput/SecureInput';
import { hasValue } from '../settingsUtils';

const TTS_PROVIDERS = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'google', label: 'Google Cloud TTS (Best for Hebrew)' },
  { value: 'elevenlabs', label: 'ElevenLabs (Multilingual)' },
];

const OPENAI_MODELS = [
  { value: 'tts-1', label: 'TTS-1 (Fast)' },
  { value: 'tts-1-hd', label: 'TTS-1 HD (Higher quality)' },
];

const OPENAI_VOICES = [
  { value: 'nova', label: 'Nova (Female, warm)' },
  { value: 'alloy', label: 'Alloy (Neutral)' },
  { value: 'echo', label: 'Echo (Male)' },
  { value: 'fable', label: 'Fable (Expressive)' },
  { value: 'onyx', label: 'Onyx (Male, deep)' },
  { value: 'shimmer', label: 'Shimmer (Female, clear)' },
];

const GOOGLE_HE_VOICES = [
  { value: 'he-IL-Wavenet-A', label: 'Hebrew Wavenet A (Female)' },
  { value: 'he-IL-Wavenet-B', label: 'Hebrew Wavenet B (Male)' },
  { value: 'he-IL-Wavenet-C', label: 'Hebrew Wavenet C (Female)' },
  { value: 'he-IL-Wavenet-D', label: 'Hebrew Wavenet D (Male) - Recommended' },
  { value: 'he-IL-Standard-A', label: 'Hebrew Standard A (Female)' },
  { value: 'he-IL-Standard-B', label: 'Hebrew Standard B (Male)' },
  { value: 'he-IL-Standard-C', label: 'Hebrew Standard C (Female)' },
  { value: 'he-IL-Standard-D', label: 'Hebrew Standard D (Male)' },
];

const GOOGLE_EN_VOICES = [
  { value: 'en-US-Wavenet-A', label: 'English Wavenet A (Male)' },
  { value: 'en-US-Wavenet-B', label: 'English Wavenet B (Male)' },
  { value: 'en-US-Wavenet-C', label: 'English Wavenet C (Female)' },
  { value: 'en-US-Wavenet-D', label: 'English Wavenet D (Male) - Recommended' },
  { value: 'en-US-Wavenet-E', label: 'English Wavenet E (Female)' },
  { value: 'en-US-Wavenet-F', label: 'English Wavenet F (Female)' },
  { value: 'en-US-Standard-A', label: 'English Standard A (Male)' },
  { value: 'en-US-Standard-B', label: 'English Standard B (Male)' },
  { value: 'en-US-Standard-C', label: 'English Standard C (Female)' },
  { value: 'en-US-Standard-D', label: 'English Standard D (Male)' },
];

const FIELD_META: FieldMeta[] = [
  {
    key: 'webchat_voice_tts_provider',
    label: 'Default TTS Provider',
    description: 'Text-to-speech provider used for all widgets (unless overridden per widget).',
    type: 'select',
    fallbackDefault: 'openai',
    options: TTS_PROVIDERS,
  },
  // OpenAI fields
  {
    key: 'webchat_voice_api_key_openai',
    label: 'OpenAI API Key (TTS)',
    description: 'API key for OpenAI TTS. Falls back to the Chat LLM OpenAI key if empty.',
    type: 'string',
    fallbackDefault: '',
    isSensitive: true,
    showWhen: d => (d['webchat_voice_tts_provider'] || 'openai') === 'openai',
  },
  {
    key: 'webchat_voice_tts_model',
    label: 'TTS Model',
    description: 'OpenAI TTS model to use.',
    type: 'select',
    fallbackDefault: 'tts-1',
    options: OPENAI_MODELS,
    showWhen: d => (d['webchat_voice_tts_provider'] || 'openai') === 'openai',
  },
  {
    key: 'webchat_voice_tts_voice',
    label: 'Default Voice',
    description: 'OpenAI voice to use for TTS.',
    type: 'select',
    fallbackDefault: 'nova',
    options: OPENAI_VOICES,
    showWhen: d => (d['webchat_voice_tts_provider'] || 'openai') === 'openai',
  },
  // Google fields
  {
    key: 'webchat_voice_google_api_key',
    label: 'Google Cloud API Key',
    description: 'Google Cloud API key with Text-to-Speech API enabled.',
    type: 'string',
    fallbackDefault: '',
    isSensitive: true,
    showWhen: d => d['webchat_voice_tts_provider'] === 'google',
  },
  {
    key: 'webchat_voice_google_voice',
    label: 'Hebrew Voice (he)',
    description: 'Google Cloud TTS voice used when the widget language is Hebrew.',
    type: 'select',
    fallbackDefault: 'he-IL-Wavenet-D',
    options: GOOGLE_HE_VOICES,
    showWhen: d => d['webchat_voice_tts_provider'] === 'google',
  },
  {
    key: 'webchat_voice_google_en_voice',
    label: 'English Voice (en)',
    description: 'Google Cloud TTS voice used when the widget language is English.',
    type: 'select',
    fallbackDefault: 'en-US-Wavenet-D',
    options: GOOGLE_EN_VOICES,
    showWhen: d => d['webchat_voice_tts_provider'] === 'google',
  },
  // ElevenLabs fields
  {
    key: 'webchat_voice_elevenlabs_api_key',
    label: 'ElevenLabs API Key',
    description: 'ElevenLabs API key from your account settings.',
    type: 'string',
    fallbackDefault: '',
    isSensitive: true,
    showWhen: d => d['webchat_voice_tts_provider'] === 'elevenlabs',
  },
  {
    key: 'webchat_voice_elevenlabs_voice_id',
    label: 'ElevenLabs Voice ID',
    description: 'Voice ID from your ElevenLabs voice library. Uses multilingual v2 model.',
    type: 'string',
    fallbackDefault: '',
    showWhen: d => d['webchat_voice_tts_provider'] === 'elevenlabs',
  },
  {
    key: 'webchat_voice_token_monthly_limit',
    label: 'Monthly Token Limit',
    description: 'Maximum total tokens allowed per calendar month across all webchat conversations.',
    type: 'number',
    fallbackDefault: 3200000,
    min: 0,
    step: 100000,
  },
];

const normalizeResponse = (raw: unknown): SettingsMap => {
  if (Array.isArray(raw)) {
    const mapped: SettingsMap = {};
    for (const item of raw) {
      if (!item || typeof item !== 'object') continue;
      const rec = item as Record<string, unknown>;
      const key = typeof rec.key === 'string' ? rec.key : '';
      if (!key) continue;
      mapped[key] = {
        value: (rec.value as string | number) ?? '',
        default: (rec.default as string | number) ?? (rec.defaultValue as string | number) ?? '',
        isSensitive: typeof rec.isSensitive === 'boolean' ? rec.isSensitive : undefined,
      };
    }
    return mapped;
  }
  if (raw && typeof raw === 'object') {
    const container = raw as Record<string, unknown>;
    if (Array.isArray(container.settings)) return normalizeResponse(container.settings);
    if (Array.isArray(container.data)) return normalizeResponse(container.data);
  }
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    const mapped: SettingsMap = {};
    for (const [key, val] of Object.entries(obj)) {
      if (!val || typeof val !== 'object') continue;
      const rec = val as Record<string, unknown>;
      mapped[key] = {
        value: (rec.value as string | number) ?? '',
        default: (rec.default as string | number) ?? (rec.defaultValue as string | number) ?? '',
        isSensitive: typeof rec.isSensitive === 'boolean' ? rec.isSensitive : undefined,
      };
    }
    return mapped;
  }
  return {};
};

const ChatVoiceSettingsTab: React.FC = () => {
  const [settings, setSettings] = useState<SettingsMap | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [initialDraft, setInitialDraft] = useState<Record<string, string>>({});
  const [sensitiveConfigured, setSensitiveConfigured] = useState<Record<string, boolean>>({});
  const [clearedSensitive, setClearedSensitive] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadSettings = async (showErrorToast = true) => {
    try {
      const raw = await settingsApi.getWebchatSettings();
      const fromServer = normalizeResponse(raw);
      const normalized: SettingsMap = { ...fromServer };
      const initial: Record<string, string> = {};
      const sensitiveState: Record<string, boolean> = {};

      for (const { key, fallbackDefault, isSensitive } of FIELD_META) {
        const rawValue = fromServer[key]?.value;
        const rawDefault = fromServer[key]?.default;
        const def = hasValue(rawDefault) ? (rawDefault as string | number) : fallbackDefault;
        const fieldIsSensitive = fromServer[key]?.isSensitive ?? isSensitive;

        if (fieldIsSensitive) {
          sensitiveState[key] = hasValue(rawValue) || hasValue(rawDefault);
          normalized[key] = { value: '', default: '' };
          initial[key] = '';
        } else {
          const value = hasValue(rawValue) ? (rawValue as string | number) : def;
          normalized[key] = { value, default: def };
          initial[key] = String(value);
        }
      }

      setSettings(normalized);
      setDraft(initial);
      setInitialDraft(initial);
      setSensitiveConfigured(sensitiveState);
      setClearedSensitive({});
    } catch {
      if (showErrorToast) toast.error('Failed to load Chat Voice settings');
    }
  };

  useEffect(() => {
    loadSettings().finally(() => setLoading(false));
  }, []);

  const isDirty = useMemo(() => {
    return FIELD_META.some(({ key }) => draft[key] !== (initialDraft[key] ?? '') || clearedSensitive[key] === true);
  }, [draft, initialDraft, clearedSensitive]);

  const handleSensitiveChange = (key: string, value: string) => {
    setDraft(d => ({ ...d, [key]: value }));
    if (value.trim()) {
      setClearedSensitive(c => ({ ...c, [key]: false }));
    }
  };

  const handleClearSensitive = (key: string) => {
    setDraft(d => ({ ...d, [key]: '' }));
    setClearedSensitive(c => ({ ...c, [key]: true }));
  };

  const handleSaveAll = async (e: React.FormEvent) => {
    e.preventDefault();
    for (const { key, label, type, min } of FIELD_META) {
      const raw = draft[key]?.trim();
      if (type === 'number') {
        const num = Number(raw);
        if (isNaN(num)) {
          toast.error(`${label}: please enter a valid number`);
          return;
        }
        if (min !== undefined && num < min) {
          toast.error(`${label}: must be >= ${min}`);
          return;
        }
      }
    }
    setSaving(true);
    try {
      const updates: Record<string, string> = {};
      for (const { key, type, isSensitive } of FIELD_META) {
        const raw = draft[key]?.trim() ?? '';
        const previous = initialDraft[key] ?? '';
        const clearRequested = clearedSensitive[key] === true;
        if (isSensitive) {
          if (!raw && !clearRequested) continue;
          if (raw) {
            updates[key] = raw;
          } else if (clearRequested) {
            updates[key] = '';
          }
          continue;
        }
        if (raw === previous) continue;
        if (type === 'number') {
          updates[key] = String(Number(raw));
        } else {
          updates[key] = raw;
        }
      }

      if (Object.keys(updates).length === 0) {
        toast.success('No changes to save');
        setSaving(false);
        return;
      }

      await Promise.all(
        Object.entries(updates).map(([key, value]) =>
          settingsApi.setValue(key, { value })
        )
      );
      await loadSettings(false);
      toast.success('Chat Voice settings saved');
    } catch {
      toast.error('Failed to save Chat Voice settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className={styles.loading}>Loading…</div>;
  if (!settings) return null;

  const visibleFields = FIELD_META.filter(f => !f.showWhen || f.showWhen(draft));

  return (
    <div className={styles.tab}>
      <div className={styles.header}>
        <div>
          <h2>Chat Voice</h2>
          <p>Default TTS provider and voice settings for all webchat widgets. Individual widgets can override these settings.</p>
        </div>
      </div>

      <form className={styles.form} onSubmit={handleSaveAll}>
        {visibleFields.map(({ key, label, description, type, isSensitive, min, max, step, options }) => (
          <div key={key} className={styles.fieldRow}>
            <div className={styles.fieldHeader}>
              <span className={styles.fieldLabel}>{label}</span>
              <span className={styles.keyBadge}>{key}</span>
            </div>
            <p className={styles.fieldDescription}>{description}</p>
            <div className={styles.fieldInputRow}>
              {type === 'select' && options ? (
                <select
                  className={styles.input}
                  value={draft[key] ?? ''}
                  onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))}
                >
                  {options.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              ) : (
                <div className={styles.fieldInputControls}>
                  <SecureInput
                    fieldType={type === 'number' ? 'number' : 'text'}
                    className={`${styles.input} ${type === 'string' ? styles.inputWide : ''}`}
                    value={draft[key] ?? ''}
                    placeholder={isSensitive && sensitiveConfigured[key] ? 'Enter a new value to replace the configured key' : undefined}
                    autoComplete={isSensitive ? 'new-password' : 'off'}
                    min={type === 'number' ? min : undefined}
                    max={type === 'number' ? max : undefined}
                    step={type === 'number' ? (step ?? 1) : undefined}
                    onChange={e => isSensitive ? handleSensitiveChange(key, e) : setDraft(d => ({ ...d, [key]: e }))}
                  />
                  {isSensitive && sensitiveConfigured[key] && (
                    <button
                      type="button"
                      className={styles.clearBtn}
                      onClick={() => handleClearSensitive(key)}
                      disabled={saving}
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}
              <span className={styles.defaultHint}>
                {isSensitive
                  ? clearedSensitive[key]
                    ? '(will be cleared)'
                    : sensitiveConfigured[key]
                      ? '(configured)'
                      : '(not set)'
                  : `default: ${settings[key]?.default}`}
              </span>
            </div>
          </div>
        ))}

        <div className={styles.footer}>
          <button type="submit" className={styles.saveBtn} disabled={!isDirty || saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatVoiceSettingsTab;
